import { useEffect, useState, FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { WSLogo, WSHeartMark } from '../components/ui';

type Phase = 'verifying' | 'set-password' | 'done' | 'error';

export function AcceptInviteScreen() {
  const [phase, setPhase] = useState<Phase>('verifying');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type');

    if (!tokenHash || type !== 'invite') {
      setError('This invitation link is missing required information. Please ask your administrator to resend the invite.');
      setPhase('error');
      return;
    }

    (async () => {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'invite',
      });
      if (error || !data.user) {
        setError(error?.message || 'This invitation link is invalid or has expired. Please ask your administrator to resend it.');
        setPhase('error');
        return;
      }
      setEmail(data.user.email ?? null);
      setPhase('set-password');
    })();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setSubmitting(false);
      setError(error.message);
      return;
    }
    await supabase.auth.signOut();
    setSubmitting(false);
    setPhase('done');
    window.history.replaceState({}, '', '/');
  }

  function goToLogin() {
    window.location.assign('/');
  }

  return (
    <div className="ws-fadein" style={{
      display: 'grid', gridTemplateColumns: '1fr 1.05fr',
      height: '100vh', width: '100%', background: 'var(--ws-bone)',
    }}>
      <div className="ws-scroll" style={{
        display: 'flex', flexDirection: 'column', padding: '40px 64px',
        position: 'relative', overflow: 'auto', background: 'var(--ws-paper)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <WSLogo size={28} />
          <a href="https://locate2u.com" style={{ fontSize: 12.5, color: 'var(--ws-mute)', textDecoration: 'none', letterSpacing: '0.04em' }}>
            locate2u.com →
          </a>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 420, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 36 }}>
            <div className="ws-eyebrow-brand" style={{ marginBottom: 14 }}>Reporting Portal · Accept invitation</div>

            {phase === 'verifying' && (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  Verifying your invitation…
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  One moment while we confirm this invitation link.
                </div>
              </>
            )}

            {phase === 'set-password' && (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  Set your password.
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  {email ? <>You are setting a password for <strong style={{ color: 'var(--ws-ink-2)' }}>{email}</strong>. </> : null}
                  Choose a password you will use to sign in from now on.
                </div>
              </>
            )}

            {phase === 'done' && (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  All set.
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  Your password has been saved. Sign in with your email and new password to access the Reporting Portal.
                </div>
              </>
            )}

            {phase === 'error' && (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  Invitation unavailable.
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  {error}
                </div>
              </>
            )}
          </div>

          {phase === 'set-password' && (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
              <div>
                <label className="ws-field-label">New password</label>
                <input
                  type="password"
                  required
                  className="ws-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>
              <div>
                <label className="ws-field-label">Confirm password</label>
                <input
                  type="password"
                  required
                  className="ws-input"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              {error && (
                <div style={{
                  fontSize: 13, color: 'var(--ws-danger)',
                  background: 'rgba(200,50,45,0.06)', padding: '10px 14px', borderRadius: 4,
                  border: '1px solid rgba(200,50,45,0.18)',
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="ws-btn ws-btn-primary"
                disabled={submitting}
                style={{ width: '100%', padding: '15px 22px', marginTop: 4 }}
              >
                {submitting ? (
                  <>
                    <svg className="ws-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="40" strokeDashoffset="20" />
                    </svg>
                    Saving…
                  </>
                ) : (
                  <>Save password &nbsp;<span style={{ letterSpacing: 0, fontSize: 14 }}>→</span></>
                )}
              </button>
            </form>
          )}

          {(phase === 'done' || phase === 'error') && (
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={goToLogin}
              style={{ width: '100%', padding: '15px 22px', marginTop: 4 }}
            >
              Continue to sign in &nbsp;<span style={{ letterSpacing: 0, fontSize: 14 }}>→</span>
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 11, color: 'var(--ws-mute-2)', letterSpacing: '0.04em', marginTop: 36,
        }}>
          <span>© Locate2u 2026 · Locate Technologies</span>
          <span style={{ display: 'flex', gap: 18 }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Status</a>
          </span>
        </div>
      </div>

      <div style={{
        position: 'relative', overflow: 'hidden',
        background: 'radial-gradient(120% 120% at 20% 0%, #1c4b82 0%, #12355B 45%, #0c2340 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 72px',
      }}>
        <WSHeartMark size={64} frontColor="#3B9EFF" backColor="#ffffff" />
        <div style={{ marginTop: 28, color: '#fff', fontSize: 40, fontWeight: 300, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
          Delivery intelligence,<br />simplified.
        </div>
        <div style={{ marginTop: 18, color: 'rgba(255,255,255,0.7)', fontSize: 15, lineHeight: 1.6, maxWidth: 420 }}>
          Track performance, cost and customer experience across your entire delivery network — powered by Locate2u.
        </div>
      </div>
    </div>
  );
}
